import { useEffect, useMemo, useState } from 'react'

import ViewPreviousHistoryModal from './ViewPreviousHistoryModal'
import useRedux from '../../../hooks/useRedux'
import useTranslations from '../../../hooks/useTranslations'
import { setPopupMessageModel } from '../../../redux/app/reducer'
import ANTDButton from '../../../shared/antd/ANTDButton'
import { ANTDDateRange } from '../../../shared/antd/ANTDDatePicker'
import ANTDForm, { useFormFn } from '../../../shared/antd/ANTDForm'
import ANTDSelect from '../../../shared/antd/ANTDSelect'
import ANTDTab from '../../../shared/antd/ANTDTab'
import ANTDTable from '../../../shared/antd/ANTDTable'
import Label from '../../../shared/Label'
import { apiParams } from '../../../utils'
import { numberFormatting } from '../../../utils/customFunctions'
import { dayJs, formatDate, SAVE_FORMAT } from '../../../utils/dayjs'
import { isEqual } from '../../../utils/javascript'
import fiscalYearSelect from '../../common/container/fiscalYearSelect.container'
import { exportToExcelApi, getStockReportListApi } from '../report.api'

const StockReport = () => {
  const { t } = useTranslations()
  const { dispatch } = useRedux()
  const form = useFormFn()
  // const [isEdit, setIsEdit] = useState({})
  const FOUR_WHEELER = 'FOUR_WHEELER'
  const TWO_WHEELER = 'TWO_WHEELER'
  const REPORT_TYPE = 'STOCK_REPORT_SHEET'
  const [activeTab, setActiveTab] = useState(FOUR_WHEELER)
  const [dataSource, setDataSource] = useState({
    [FOUR_WHEELER]: [],
    [TWO_WHEELER]: [],
  })
  const [loader, setLoader] = useState(false)
  const [viewPreviousHistory, setViewPreviousHistory] = useState({
    open: false,
    data: [],
  })
  const [dateRange, setDateRange] = useState({})
  const [selectedDate, setSelectedDate] = useState({
    date: null,
    range: '',
  })
  const { fiscalYearSelector } = fiscalYearSelect({})

  // const [rowF, setRowF] = useState({
  //   [FOUR_WHEELER]: {},
  //   [TWO_WHEELER]: {},
  // })
  const saveFormat = 'DD/MM/YYYY'

  useEffect(() => {
    if (fiscalYearSelector.value) {
      if (dateRange?.from && dateRange?.to) {
        const startDate = dayJs(dateRange?.from, saveFormat)
          .year(fiscalYearSelector.value)
          .format(saveFormat)
        const endDate = dayJs(dateRange?.to, saveFormat)
          .year(fiscalYearSelector.value)
          .format(saveFormat)

        setDateRange({
          from: startDate,
          to: endDate,
        })
      } else {
        const startDate = dayJs().subtract(30, 'day').format(saveFormat)
        const endDate = dayJs().format(saveFormat)
        setDateRange({
          from: startDate,
          to: endDate,
        })
      }
    }
  }, [fiscalYearSelector.value])

  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      getStockReportData()
    }
  }, [dateRange?.from, dateRange?.to, activeTab])

  const getStockReportData = async () => {
    setLoader(true)
    const payload = {
      vehicleType: activeTab,
      fiscalYear: fiscalYearSelector?.value,
      fromDate: dateRange?.from,
      toDate: dateRange?.to,
    }

    const response = await getStockReportListApi({
      params: apiParams({ params: payload }),
    })
    setLoader(false)

    if (response?.data?.scrapData) {
      setDataSource(prev => ({
        ...prev,
        [activeTab]: response?.data?.scrapData,
      }))
    }
  }

  const handleDateRangeChange = ([start, end], _, { range }) => {
    setSelectedDate({
      date: isEqual(range, 'start') ? start : end,
      range: range,
    })

    // Calculate the difference in days between start and end
    const daysDiff = end.diff(start, 'day')

    let startDate = start.format(saveFormat)
    let endDate = end.format(saveFormat)

    if (daysDiff > 30) {
      if (isEqual(range, 'start')) {
        endDate = start.add(30, 'day').format(saveFormat)
      } else {
        startDate = end.subtract(30, 'day').format(saveFormat)
      }
    }

    setDateRange(prev => ({
      ...prev,
      from: startDate,
      to: endDate,
    }))
  }

  const onOpenPanelChange = () => {
    if (selectedDate?.date) {
      setSelectedDate({
        date: null,
        range: '',
      })
    }
  }

  const datesColumns = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return []
    const dates = []
    let currentDate = dayJs(dateRange?.from, saveFormat)
    const end = dayJs(dateRange?.to, saveFormat)

    while (currentDate.isBefore(end) || currentDate.isSame(end, 'day')) {
      dates.push({
        title: currentDate.format('Do MMM'),
        dataIndex: currentDate.format('Do MMMM'),
      })
      currentDate = currentDate.add(1, 'day')
    }

    return dates
  }, [dateRange?.from, dateRange?.to])

  const disabledRange = current => {
    if (!selectedDate?.date) return false

    const startDate = isEqual(selectedDate?.range, 'start')
      ? selectedDate?.date
      : selectedDate?.date?.subtract(30, 'day')

    const maxEndDate = startDate.add(30, 'day')
    const minEndDate = startDate.subtract(30, 'day')

    // Disable dates that are more than 30 days away from the start date
    return (
      current.isBefore(minEndDate, 'day') || current.isAfter(maxEndDate, 'day')
    )
  }

  // const onEditClick = ({ colHeading, value }) => {
  //   setIsEdit(prev => ({ ...prev, [colHeading]: true }))
  //   if (!value) return
  //   form.setFieldsValue({ [colHeading]: value })
  // }
  // const onSaveClick = ({ colHeading, prevValue }) => {
  //   const updatedValue = form.getFieldValue(colHeading)
  //   if (updatedValue) {
  //     // const diffValue = updatedValue - prevValue

  //     setRowF(prev => ({
  //       ...prev,
  //       [activeTab]: {
  //         ...prev[activeTab],
  //         [colHeading]: updatedValue,
  //       },
  //     }))
  //   }
  //   setIsEdit(prev => ({ ...prev, [colHeading]: false }))
  // }

  const firstColumnData = [
    {
      key: 'DEALER_DIRECT_INDIVIDUAL_COUNT',
      title: 'A. Dealer/ Individual',
      description:
        'All vehicles received at MSTI on a day from Dealer and Individual together',
    },
    {
      key: 'COLLECTION_CENTER_COUNT',
      title: 'B. Aggregation Center',
      description:
        'All vehicles received at MSTI on a day from all Aggregation Centers',
    },
    {
      key: 'MSIL_MSG_INBOUND_COUNT',
      title: 'C. Inbound from MSIL/ SMG',
      description:
        'All test vehicles received at MSTI on a day from MSIL and Suzuki Motor Gujarat',
    },
    {
      key: 'PRODUCER_INBOUND_COUNT',
      title: 'D. Inbound from Honda/Suzuki',
      description:
        'All test vehicles received at MSTI on a day from Honda and Suzuki',
    },
    {
      key: 'PROCESSED_COUNT',
      title: 'E. Actual Production',
      description: 'Number of ELVs processed at MSTI on a day',
    },
    {
      key: 'TOTAL_COUNT',
      title: 'Stock at Plant',
      description: 'Stock at Plant on previous day + [A+B+C+D-E]',
    },
  ]

  const columns = [
    {
      title: t('rpt_Title'),
      key: 'rpt_Title',
      dataIndex: 'title',
      width: '150px',
    },
    // {
    //   title: t('inv_Description'),
    //   key: 'inv_Description',
    //   dataIndex: 'description',
    //   width: '250px',
    // },
    ...datesColumns.map(date => ({
      title: date?.title,
      key: date?.dataIndex,
      dataIndex: date?.dataIndex,
      className: 'nowrap',
      render: (rowData, record, rowIndex) => {
        const cell = rowData?.[firstColumnData?.[rowIndex]?.key]
        return cell == null ? '-' : numberFormatting(cell)
        // const rowFValue = rowF?.[activeTab]?.[date]
        // return isEqual(record?.key, 'F') ? (
        //   rowFValue || isEqual(rowFValue, 0) ? (
        //     <div className="d-flex">
        //       <span className="mr-5">
        //         {isEdit?.[date] ? (
        //           <ANTDFormItem noStyle name={date}>
        //             <ANTDInputNumber />
        //           </ANTDFormItem>
        //         ) : (
        //           rowFValue
        //         )}
        //       </span>
        //       <span className="d-flex">
        //         {isEdit?.[date] ? (
        //           <SaveOutlined
        //             onClick={() =>
        //               onSaveClick({ colHeading: date, prevValue: rowFValue })
        //             }
        //           />
        //         ) : (
        //           <EditOutlined
        //             onClick={() =>
        //               onEditClick({ colHeading: date, value: rowFValue })
        //             }
        //           />
        //         )}
        //       </span>
        //     </div>
        //   ) : (
        //     rowF?.[activeTab]?.[date] ?? '-'
        //   )
        // ) : (
        //   rowData ?? '-'
        // )
      },
    })),
  ]

  const handleTabChange = tab => {
    setActiveTab(tab)
    // setIsEdit({})
    form.resetFields()
  }

  const onGenerateReport = async () => {
    const payload = {
      type: REPORT_TYPE,
      vehicleType: activeTab,
      fiscalYear: fiscalYearSelector?.value,
      from: dateRange?.from,
      to: dateRange?.to,
    }

    const response = await exportToExcelApi({ payload })
    if (response?.data?.success) {
      dispatch(
        setPopupMessageModel({
          open: true,
          message: t('msg_ReportGenerated'),
          success: true,
        }),
      )
    }
  }

  return (
    <>
      <ANTDTab
        size="small"
        activeKey={activeTab}
        items={[
          {
            label: t('rpt_4wheelers'),
            key: FOUR_WHEELER,
          },
          {
            label: t('rpt_2wheelers'),
            key: TWO_WHEELER,
          },
        ]}
        centered
        onChange={handleTabChange}
      />

      <div className="d-flex justify-content-end">
        <div>
          <div>
            <Label text={t('txt_FiscalYear')} />
            <ANTDSelect {...fiscalYearSelector} className="w-100 mt-5" />
          </div>
          <div className="mt-10">
            <ANTDDateRange
              {...{
                format: SAVE_FORMAT,
                allowClear: false,
                value: [
                  formatDate(dateRange?.from, saveFormat),
                  formatDate(dateRange?.to, saveFormat),
                ],
                minDate: formatDate(dateRange?.min, saveFormat),
                maxDate: formatDate(dateRange?.max, saveFormat),
                // onChange: handleDateRangeChange,
                onCalendarChange: handleDateRangeChange,
                disabledDate: current => disabledRange(current),
                onOpenChange: onOpenPanelChange,
              }}
            />
            <ANTDButton
              type="primary"
              className="ml-10 mt-10"
              onClick={onGenerateReport}
            >
              {t('btn_GenerateStockReport')}
            </ANTDButton>
            <ANTDButton
              type="primary"
              className="ml-10 mt-10"
              onClick={() =>
                setViewPreviousHistory({
                  open: true,
                  type: REPORT_TYPE,
                  vehicleType: activeTab,
                })
              }
            >
              {t('btn_ViewPreviousHistory')}
            </ANTDButton>
          </div>
        </div>
      </div>
      <ANTDForm initialValues={{}} form={form}>
        {dateRange?.from && dateRange?.to && (
          <ANTDTable
            className="mt-15"
            tableLayout="fixed"
            loading={loader}
            columns={columns}
            dataSource={firstColumnData?.map(item => ({
              ...item,
              ...dataSource?.[activeTab],
            }))}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        )}
      </ANTDForm>

      <ViewPreviousHistoryModal
        open={viewPreviousHistory.open}
        onClose={() => setViewPreviousHistory({ open: false })}
        payload={{
          sheetType: REPORT_TYPE,
          vehicleType: activeTab,
        }}
      />
    </>
  )
}

export default StockReport
